import { type ReactNode, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { BootScreen } from "../components/BootScreen";
import { useAuthSession } from "./session";

const WORKSPACE_ONBOARDING_PATH = "/onboarding/workspace";

/* Covers the initial session fetch with the boot ceremony, then fades it out
 * over the routed content. The ceremony only runs when the gate mounts while
 * the session is still resolving (a hard page load), so client-side
 * navigation and later session refreshes never replay it. */
function SessionBootGate({ isLoading, children }: { isLoading: boolean; children: ReactNode }) {
	const [booted, setBooted] = useState(!isLoading);

	return (
		<>
			{booted || !isLoading ? children : null}
			{!booted && <BootScreen done={!isLoading} onExited={() => setBooted(true)} />}
		</>
	);
}

export function ProtectedRoute() {
	const { isAuthenticated, isLoading, workspaces } = useAuthSession();
	const location = useLocation();
	const hasWorkspace = workspaces.length > 0;
	const isOnWorkspaceOnboarding = location.pathname === WORKSPACE_ONBOARDING_PATH;

	if (!isLoading) {
		if (!isAuthenticated) {
			const redirectTarget = `${location.pathname}${location.search}`;
			return <Navigate to={`/login?redirect=${encodeURIComponent(redirectTarget)}`} replace />;
		}

		if (!hasWorkspace && !isOnWorkspaceOnboarding) {
			return <Navigate to={WORKSPACE_ONBOARDING_PATH} replace />;
		}

		if (hasWorkspace && isOnWorkspaceOnboarding) {
			return <Navigate to="/agents" replace />;
		}
	}

	return (
		<SessionBootGate isLoading={isLoading}>
			<Outlet />
		</SessionBootGate>
	);
}

export function PublicOnlyRoute() {
	const { isAuthenticated, isLoading } = useAuthSession();

	if (!isLoading && isAuthenticated) {
		return <Navigate to="/agents" replace />;
	}

	return (
		<SessionBootGate isLoading={isLoading}>
			<Outlet />
		</SessionBootGate>
	);
}
